import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useCreateRelationshipFromDraft } from '~/hooks/useCreateRelationshipFromDraft';
import { useSetWorksAtOptimisticUpdate } from '~/hooks/useSetWorksAtOptimisticUpdate';
import { useLocaleKeys } from '~/i18n';
import { errorTextsPath } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
export const GetUseScheduledProposalTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            UseScheduledProposalTID: {
                CancelSchedulingModal: 'cancel-scheduling-modal'
            }
        }
    });
export const useScheduledProposalActions = ()=>{
    const { t: scheduledProposalTexts } = useLocaleKeys('hooks.scheduledProposal');
    const { UseScheduledProposalTID } = useMemo(()=>GetUseScheduledProposalTID(), []);
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const { api } = useStoreActions();
    const navigate = useNavigate();
    const { browser } = useIoContext();
    const { logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const createRelationshipFromDraft = useCreateRelationshipFromDraft();
    const updateWorksAt = useSetWorksAtOptimisticUpdate();
    const sendNow = async (param)=>{
        let { draftId, isSampleRelationship, refresh } = param;
        overlayContext.stopLoader('send-now');
        logBI(11353);
        const result = await handleRequest(createRelationshipFromDraft(draftId, {
            isSampleRelationship
        }));
        if (result.success) {
            updateWorksAt(false, false);
            overlayContext.stopLoader('send-now');
            overlayContext.showSuccessToast(scheduledProposalTexts.proposalSentSuccess(), 'send-now-success-toast');
            refresh();
        } else {
            const { text } = result.error.createRelationshipErrorText(errorTexts);
            overlayContext.showErrorToast(text, 'create-relationship-error');
            overlayContext.stopLoader('send-now');
            return;
        }
    };
    const cancelScheduledProposal = async (param)=>{
        let { draftId, refresh } = param;
        overlayContext.startLoader('cancel-scheduled-proposal');
        logBI(11354);
        const result = await handleRequest(api.draft.cancelScheduledProposal({
            draftId
        }));
        if (result.success) {
            overlayContext.showSuccessToast(scheduledProposalTexts.schedulingCanceledSuccess(), 'cancel-scheduled-proposal-success-toast');
            overlayContext.stopLoader('cancel-scheduled-proposal');
            refresh();
        } else {
            overlayContext.showErrorToast(errorTexts.unexpected(), 'cancel-scheduled-proposal-error');
            overlayContext.stopLoader('cancel-scheduled-proposal');
            return;
        }
    };
    const handleCancelSchedulingAndEdit = (target, dismissModal)=>{
        cancelScheduledProposal(target);
        dismissModal(true);
        if (browser.getPathname() !== routes.draft(target.draftId)) {
            navigate(routes.draft(target.draftId));
        }
    };
    const handleCloseCancelSchedulingModal = (target, dismissModal)=>{
        var _target_onCloseCancelSchedulingModal;
        (_target_onCloseCancelSchedulingModal = target.onCloseCancelSchedulingModal) === null || _target_onCloseCancelSchedulingModal === void 0 ? void 0 : _target_onCloseCancelSchedulingModal.call(target);
        dismissModal(false);
    };
    const showSwitchToEditModal = (target)=>{
        overlayContext.showModal({
            biName: 'scheduled_proposal_cancel_to_edit_confirm',
            closeOnClickOutside: false,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'use_scheduled_proposal_confirm'
                        },
                        secondaryButton: {
                            name: 'use_scheduled_proposal_cancel'
                        }
                    },
                    title: scheduledProposalTexts.cancelToEdit(),
                    skin: "danger",
                    testId: UseScheduledProposalTID.CancelSchedulingModal(),
                    primaryButtonLabel: scheduledProposalTexts.cancelSchedulingAndEdit(),
                    secondaryButtonLabel: commonButtons.cancel(),
                    onPrimaryButtonClick: ()=>handleCancelSchedulingAndEdit(target, dismissModal),
                    onSecondaryButtonClick: ()=>handleCloseCancelSchedulingModal(target, dismissModal),
                    onCloseButtonClick: ()=>handleCloseCancelSchedulingModal(target, dismissModal),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: scheduledProposalTexts.cancelSchedulingContent()
                    })
                });
            }
        });
    };
    return {
        sendNow,
        cancelScheduledProposal,
        handleCancelSchedulingAndEdit,
        showSwitchToEditModal
    };
};
export const useScheduledProposal = (props)=>{
    const actions = useScheduledProposalActions();
    return {
        sendNow: ()=>actions.sendNow(props),
        cancelScheduledProposal: ()=>actions.cancelScheduledProposal(props),
        handleCancelSchedulingAndEdit: (dismissModal)=>actions.handleCancelSchedulingAndEdit(props, dismissModal),
        showSwitchToEditModal: ()=>actions.showSwitchToEditModal(props)
    };
};
